import { AnimatePresence, motion } from "framer-motion";
import { MdClose } from "react-icons/md";
import { FiExternalLink } from "react-icons/fi";

const SpringModal = ({
  isOpen,
  setIsOpen,
  title,
  description,
  imgSrc,
  tech,
  projectLinks,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
          className="fixed inset-0 z-50 px-4 py-8 md:py-12 bg-slate-950/80 backdrop-blur-xl overflow-y-scroll flex justify-center items-start cursor-pointer"
        >
          {/* Close button */}
          <motion.button
            onClick={() => setIsOpen(false)}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.1, rotate: 90 }}
            transition={{ duration: 0.2 }}
            className="fixed top-6 right-6 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <MdClose className="text-2xl" />
          </motion.button>

          {/* Modal container */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl mt-16 md:mt-0 cursor-auto"
          >
            {/* Glass-morphism card */}
            <div className="relative rounded-3xl overflow-hidden bg-slate-900/95 backdrop-blur-2xl border border-white/10 shadow-2xl">
              {/* Header image section */}
              <div className="relative h-64 md:h-96 overflow-hidden">
                <motion.img
                  initial={{ scale: 1.2, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6 }}
                  alt={title}
                  className="w-full h-full object-cover"
                  src={imgSrc}
                />

                {/* Dark gradient overlay for title readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />

                {/* Title overlay on image */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="absolute bottom-0 left-0 right-0 p-8"
                >
                  <h2 className="text-4xl md:text-5xl text-white font-bold mb-4 drop-shadow-lg">
                    {title}
                  </h2>
                </motion.div>
              </div>

              {/* Content section */}
              <div className="p-8 md:p-12">
                {/* Technology tags */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex flex-wrap gap-2 mb-8"
                >
                  {tech.map((techItem, index) => (
                    <motion.span
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 + index * 0.05 }}
                      className="px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/30 text-violet-300 text-sm font-medium backdrop-blur-sm"
                    >
                      {techItem}
                    </motion.span>
                  ))}
                </motion.div>

                {/* Description */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="space-y-4 mb-8"
                >
                  {description.map((para, index) => (
                    <motion.p
                      key={index}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 + index * 0.1 }}
                      className="text-slate-300 leading-relaxed text-base"
                    >
                      {para}
                    </motion.p>
                  ))}
                </motion.div>

                {/* Project links */}
                {projectLinks && projectLinks.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-2">
                      Project Links
                      <span className="text-violet-500">.</span>
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      {projectLinks.map((link, index) => (
                        <motion.a
                          key={index}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.8 + index * 0.1 }}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                          target="_blank"
                          rel="nofollow noreferrer"
                          href={link.url}
                          className="group px-6 py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-medium transition-all duration-300 flex items-center gap-2 shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40"
                        >
                          {link.icon}
                          <span>{link.label}</span>
                          <FiExternalLink className="opacity-75 group-hover:opacity-100 transition-opacity" />
                        </motion.a>
                      ))}
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Decorative gradient elements */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-violet-500/20 to-purple-500/20 blur-3xl pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-cyan-500/10 to-blue-500/10 blur-3xl pointer-events-none" />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SpringModal;